import React, { useState, useMemo, useEffect } from "react";
import Nav from "../components/Nav";
import ResourceReportDialog from "../components/ResourceReportDialog";
import { resources, counties, resourceCategories } from "../data/vetResources";
import SEO, {
  generateWebPageSchema,
  generateFAQSchema,
} from "../components/SEO";
import {
  faqSections,
  getArticleCategoryUrl,
  getResourceCategoryUrl,
} from "../utils/seoPaths";

const VeteranResources = () => {
  const [selectedCounty, setSelectedCounty] = useState("All");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  useEffect(() => {
    if (typeof window !== "undefined") window.prerenderReady = true;
  }, []);

  // Helper function to get categories for a resource (handles both string and array)
  const getResourceCategories = (resource) => {
    if (Array.isArray(resource.category)) {
      return resource.category;
    }
    return [resource.category];
  };

  // Helper function to check if resource matches category
  const resourceMatchesCategory = (resource, category) => {
    if (category === "All") return true;
    const resourceCategories = getResourceCategories(resource);
    return resourceCategories.includes(category);
  };

  // Filter resources based on selections
  const filteredResources = useMemo(() => {
    return resources.filter((resource) => {
      // County filter
      const countyMatch =
        selectedCounty === "All" ||
        resource.counties.includes("All") ||
        resource.counties.includes(selectedCounty);

      // Category filter - now handles both string and array categories
      const categoryMatch = resourceMatchesCategory(resource, selectedCategory);

      // Search filter
      const searchMatch =
        searchTerm === "" ||
        resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.services.some((service) =>
          service.toLowerCase().includes(searchTerm.toLowerCase())
        );

      return countyMatch && categoryMatch && searchMatch;
    });
  }, [selectedCounty, selectedCategory, searchTerm]);

  // Group resources by category - now handles multiple categories per resource
  const groupedResources = useMemo(() => {
    const grouped = {};

    filteredResources.forEach((resource) => {
      const resourceCategories = getResourceCategories(resource);

      resourceCategories.forEach((category) => {
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(resource);
      });
    });

    // Sort resources alphabetically by name within each category
    Object.keys(grouped).forEach((category) => {
      grouped[category].sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
    });

    return grouped;
  }, [filteredResources]);

  // When searching, auto-expand all categories so results are visible
  useEffect(() => {
    if (searchTerm) {
      setExpandedCategories(new Set(Object.keys(groupedResources)));
    }
  }, [searchTerm, groupedResources]);

  const toggleCard = (id) => {
    // Store current scroll position
    const currentScrollY = window.scrollY;

    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedCards(newExpanded);

    // Restore scroll position after state update
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollY);
    });
  };

  const toggleCategory = (category) => {
    // Store current scroll position
    const currentScrollY = window.scrollY;

    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);

    // Restore scroll position after state update
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollY);
    });
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    // Remove any non-digit characters and format
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    }
    return phone; // Return original if not standard format
  };

  const ResourceCard = ({ resource }) => {
    const isExpanded = expandedCards.has(resource.id);
    const hasMultiplePhones =
      resource.phone2 || resource.phone3 || resource.phone4;

    // NEW: consider any contact channel as "contact info"
    const hasContactInfo = Boolean(
      resource.phone ||
        hasMultiplePhones ||
        resource.email ||
        resource.website ||
        resource.address ||
        (Array.isArray(resource.contacts) && resource.contacts.length > 0)
    );

    return (
      <div className="bg-gray-800 border border-gray-600 rounded-lg p-4 hover:border-blue-400 transition-colors">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h4 className="text-xl font-bold text-white mb-1">
              {resource.name}
            </h4>
            {resource.address && (
              <p className="text-gray-400 text-sm mb-2">{resource.address}</p>
            )}
          </div>

          {/* CHANGED: use hasContactInfo instead of only phone */}
          {hasContactInfo && (
            <button
              onClick={() => toggleCard(resource.id)}
              className="ml-4 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded text-sm font-semibold transition-colors"
            >
              {isExpanded ? "Hide Details" : "Contact Info"}
            </button>
          )}
        </div>

        {/* Services Tags */}
        {resource.services.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {resource.services.map((service, index) => (
              <span
                key={index}
                className="bg-gray-700 text-blue-300 px-2 py-1 rounded text-xs font-medium"
              >
                {service}
              </span>
            ))}
          </div>
        )}

        {/* Description */}
        {resource.description && (
          <p className="text-gray-300 text-sm mb-3 leading-relaxed">
            {resource.description}
          </p>
        )}

        {/* Expanded Contact Details */}
        {isExpanded && (
          <div className="border-t border-gray-600 pt-3 mt-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Phone Numbers */}
              <div>
                {resource.phone && (
                  <div className="mb-2">
                    <span className="text-blue-400 font-semibold text-sm">
                      Phone:{" "}
                    </span>
                    <a
                      href={`tel:${resource.phone}`}
                      className="text-white hover:text-blue-400 transition-colors"
                    >
                      {formatPhoneNumber(resource.phone)}
                    </a>
                  </div>
                )}
                {resource.phone2 && (
                  <div className="mb-2">
                    <span className="text-blue-400 font-semibold text-sm">
                      Phone 2:{" "}
                    </span>
                    <a
                      href={`tel:${resource.phone2}`}
                      className="text-white hover:text-blue-400 transition-colors"
                    >
                      {formatPhoneNumber(resource.phone2)}
                    </a>
                  </div>
                )}
                {resource.phone3 && (
                  <div className="mb-2">
                    <span className="text-blue-400 font-semibold text-sm">
                      Phone 3:{" "}
                    </span>
                    <a
                      href={`tel:${resource.phone3}`}
                      className="text-white hover:text-blue-400 transition-colors"
                    >
                      {formatPhoneNumber(resource.phone3)}
                    </a>
                  </div>
                )}
                {resource.phone4 && (
                  <div className="mb-2">
                    <span className="text-blue-400 font-semibold text-sm">
                      Phone 4:{" "}
                    </span>
                    <a
                      href={`tel:${resource.phone4}`}
                      className="text-white hover:text-blue-400 transition-colors"
                    >
                      {formatPhoneNumber(resource.phone4)}
                    </a>
                  </div>
                )}
              </div>

              {/* Other Contact Info */}
              <div>
                {resource.website && (
                  <div className="mb-2">
                    <span className="text-blue-400 font-semibold text-sm">
                      Website:{" "}
                    </span>
                    <a
                      href={resource.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:text-blue-400 transition-colors break-all"
                    >
                      Visit Website →
                    </a>
                  </div>
                )}
                {resource.email && (
                  <div className="mb-2">
                    <span className="text-blue-400 font-semibold text-sm">
                      Email:{" "}
                    </span>
                    <a
                      href={`mailto:${resource.email}`}
                      className="text-white hover:text-blue-400 transition-colors break-all"
                    >
                      {resource.email}
                    </a>
                  </div>
                )}
                {resource.counties.length > 1 && (
                  <div className="mb-2">
                    <span className="text-blue-400 font-semibold text-sm">
                      Serves:{" "}
                    </span>
                    <span className="text-gray-300 text-sm">
                      {resource.counties.filter((c) => c !== "All").join(", ")}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            {resource.notes && (
              <div className="mt-3 p-3 bg-gray-700 rounded border-l-4 border-blue-500">
                <span className="text-blue-400 font-semibold text-sm">
                  Note:{" "}
                </span>
                <span className="text-gray-300 text-sm">{resource.notes}</span>
              </div>
            )}
          </div>
        )}
        <ResourceReportDialog
          resource={resource}
          directory="veteran"
          accent="blue"
        />
      </div>
    );
  };

  // Determine if categories should be collapsible
  const shouldUseCollapsibleCategories = selectedCategory === "All";

  return (
    <div className="min-h-screen bg-gray-900 text-white font-serif">
      <SEO
        title="Veteran Resources & Benefits in New Hampshire - Support for Veterans"
        description="Comprehensive directory of veteran resources, benefits, healthcare, housing assistance, and support services across New Hampshire. Benefits, crisis support, and specialized services for veterans and families."
        canonicalUrl="https://www.recoverycompassnh.com/veterans"
        ogType="website"
        ogImage="https://www.recoverycompassnh.com/og-veterans.png"
        ogImageAlt="Veteran resources and benefits directory for New Hampshire"
        breadcrumbs={[
          { name: "Home", url: "https://www.recoverycompassnh.com/" },
          {
            name: "Veteran Resources",
            url: "https://www.recoverycompassnh.com/veterans",
          },
        ]}
        schema={[
          generateWebPageSchema({
            title: "Veteran Resources & Benefits in New Hampshire",
            description:
              "Comprehensive directory of veteran resources, benefits, healthcare, housing assistance, and support services across New Hampshire.",
            url: "https://www.recoverycompassnh.com/veterans",
            aboutName: "Veteran Support Services",
            aboutDescription:
              "VA benefits, healthcare, housing, employment, and crisis services for veterans in New Hampshire",
            audienceType: "Veterans and military families in New Hampshire",
          }),
          generateFAQSchema(faqSections.veterans),
        ]}
      />
      <Nav />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 border-b border-gray-600">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="text-center">
            <div className="flex justify-center items-center mb-6">
              <h1 className="text-5xl font-bold text-white">
                VETERAN RESOURCES
              </h1>
            </div>
            <div className="max-w-4xl mx-auto">
              <p className="text-blue-100 text-xl mb-6 leading-relaxed">
                Thank you for your service. You've served our country with
                honor, and now it's our turn to serve you. This comprehensive
                directory connects you with benefits, healthcare, housing,
                employment, and support services specifically designed for
                veterans and their families throughout New Hampshire.
              </p>
              <div className="bg-blue-800/30 border border-blue-600/50 rounded-lg p-6 backdrop-blur-sm">
                <div className="flex items-center justify-center mb-3">
                  <svg
                    className="w-8 h-8 text-red-400 mr-3"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <h2 className="text-2xl font-semibold text-blue-200">
                    You Are Not Alone
                  </h2>
                  <svg
                    className="w-8 h-8 text-red-400 ml-3"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </div>
                <p className="text-blue-100 text-lg">
                  Whether you're transitioning to civilian life, seeking
                  healthcare, looking for housing assistance, or need someone to
                  talk to, these resources are here for you. Every service
                  listed understands the unique challenges veterans face and is
                  committed to providing the support you've earned.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Emergency Resources Bar */}
      <div className="bg-red-900/80 border-b border-red-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center">
            <div className="flex items-center">
              <svg
                className="w-6 h-6 text-red-300 mr-2"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 10H15.5L13.5 7.5C13.1 7.1 12.6 6.9 12 6.9S10.9 7.1 10.5 7.5L8.5 10H7C6.4 10 6 10.4 6 11S6.4 12 7 12H8.5L10.5 14.5C10.9 14.9 11.4 15.1 12 15.1S13.1 14.9 13.5 14.5L15.5 12H17C17.6 12 18 11.6 18 11S17.6 10 17 10Z" />
              </svg>
              <span className="text-red-100 font-semibold">
                Crisis Support:
              </span>
            </div>
            <a
              href="tel:1-800-273-8255"
              className="bg-red-700 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
            >
              Veterans Crisis Line: 1-800-273-8255 (Press 1)
            </a>
            <a
              href="tel:988"
              className="bg-red-700 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
            >
              Call 988 or Text 838255
            </a>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-gray-800 border-b border-gray-600 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            {/* Search Bar */}
            <div className="flex-1 min-w-0">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search veteran resources, benefits, or services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-gray-700 border-2 border-gray-600 text-white px-4 py-3 pr-10 rounded-lg focus:border-blue-500 focus:outline-none placeholder-gray-400"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* County Filter */}
            <div className="w-full lg:w-48">
              <select
                value={selectedCounty}
                onChange={(e) => setSelectedCounty(e.target.value)}
                className="w-full bg-gray-700 border-2 border-gray-600 text-white px-4 py-3 rounded-lg focus:border-blue-500 focus:outline-none"
              >
                {counties.map((county) => (
                  <option key={county} value={county}>
                    {county}
                  </option>
                ))}
              </select>
            </div>

            {/* Category Filter */}
            <div className="w-full lg:w-64">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full bg-gray-700 border-2 border-gray-600 text-white px-4 py-3 rounded-lg focus:border-blue-500 focus:outline-none"
              >
                <option value="All">All Categories</option>
                {resourceCategories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-center">
            <span className="text-gray-400 text-sm">
              Showing {filteredResources.length} of {resources.length} veteran
              resources
              {selectedCounty !== "All" && ` in ${selectedCounty} County`}
              {selectedCategory !== "All" && ` for ${selectedCategory}`}
            </span>
            {shouldUseCollapsibleCategories &&
              Object.keys(groupedResources).length > 1 && (
                <div className="mt-2">
                  <span className="text-blue-400 text-sm">
                    Click category headers to expand/collapse sections
                  </span>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Resources Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {Object.keys(groupedResources).length === 0 ? (
          // No Results
          <div className="text-center py-12">
            <div className="bg-gray-800 border border-gray-600 rounded-lg p-8 max-w-md mx-auto">
              <h3 className="text-xl font-bold text-white mb-2">
                No Resources Found
              </h3>
              <p className="text-gray-400 mb-4">
                Try adjusting your search criteria or filters to find veteran
                resources.
              </p>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setSelectedCounty("All");
                  setSelectedCategory("All");
                }}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded font-semibold transition-colors"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        ) : (
          // Results by Category
          Object.entries(groupedResources).map(([category, resourceList]) => {
            const isCategoryExpanded = expandedCategories.has(category);
            const categoryResourceCount = resourceList.length;

            return (
              <div key={category} className="mb-12">
                {/* Category Header - Clickable when using collapsible mode */}
                <div className="mb-6">
                  {shouldUseCollapsibleCategories ? (
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full text-left group focus:outline-none"
                    >
                      <div className="flex items-center justify-between border-b-2 border-blue-500 pb-2 group-hover:border-blue-400 transition-colors">
                        <h2 className="text-3xl font-bold text-blue-400 group-hover:text-blue-300 transition-colors">
                          {category.toUpperCase()}
                        </h2>
                        <div className="flex items-center gap-3">
                          <span className="text-gray-400 text-sm">
                            {categoryResourceCount} resource
                            {categoryResourceCount !== 1 ? "s" : ""}
                          </span>
                          <div className="flex items-center">
                            <svg
                              className={`w-6 h-6 text-blue-400 transition-transform duration-200 ${
                                isCategoryExpanded ? "rotate-90" : ""
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </button>
                  ) : (
                    <h2 className="text-3xl font-bold text-blue-400 mb-2 border-b-2 border-blue-500 pb-2">
                      {category.toUpperCase()}
                    </h2>
                  )}
                </div>

                {/* Category Content - Show if not collapsible or if expanded */}
                {(!shouldUseCollapsibleCategories || isCategoryExpanded) && (
                  <div className="animate-in slide-in-from-top-2 duration-200">
                    {/* Resource Cards Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {resourceList.map((resource) => (
                        <ResourceCard key={resource.id} resource={resource} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Support Footer */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 border-t border-blue-700 mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            💙 Your Service Matters, Your Wellbeing Matters
          </h3>
          <p className="text-blue-100 mb-6 max-w-3xl mx-auto text-lg">
            If you need immediate assistance or can't find what you're looking
            for, don't hesitate to reach out. There are people ready to help,
            24/7.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:1-800-273-8255"
              className="bg-red-600 hover:bg-red-500 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Veterans Crisis Line: 1-800-273-8255
            </a>
            <a
              href="tel:211"
              className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Call 2-1-1 for General Resources
            </a>
            <a
              href="/contact"
              className="border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-gray-900 px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="border-b-2 border-blue-500 pb-3 mb-8">
          <h2 className="text-3xl font-bold text-white">
            VETERAN RESOURCES FAQ
          </h2>
        </div>
        <div className="space-y-6">
          {faqSections.veterans.map((faq) => (
            <article
              key={faq.question}
              className="bg-gray-800 border border-gray-600 rounded-lg p-6"
            >
              <h3 className="text-xl font-semibold text-blue-300 mb-3">
                {faq.question}
              </h3>
              <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
            </article>
          ))}
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-gray-800 border-t border-gray-600 text-white mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-6">
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">CONTACT US</h5>
              <p className="text-sm text-gray-300">
                <br />
                Laconia, NH 03246
                <br />
              </p>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">SECTIONS</h5>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>
                  <a
                    href={getArticleCategoryUrl("unsung-heroes")}
                    className="hover:text-cyan-400"
                  >
                    Healthcare Heroes
                  </a>
                </li>
                <li>
                  <a
                    href={getArticleCategoryUrl("stories-of-recovery")}
                    className="hover:text-cyan-400"
                  >
                    Recovery Stories
                  </a>
                </li>
                <li>
                  <a href="/resources" className="hover:text-cyan-400">
                    Resources
                  </a>
                </li>
                <li>
                  <a
                    href={getArticleCategoryUrl("blog-posts")}
                    className="hover:text-cyan-400"
                  >
                    Blog
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">RESOURCES</h5>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>
                  <a
                    href={getResourceCategoryUrl("Primary SUD Providers")}
                    className="hover:text-cyan-400"
                  >
                    Treatment Centers
                  </a>
                </li>
                <li>
                  <a
                    href={getResourceCategoryUrl(
                      "Online Resources and Support Groups"
                    )}
                    className="hover:text-cyan-400"
                  >
                    Support Groups
                  </a>
                </li>
                <li>
                  <a
                    href={getResourceCategoryUrl("Crisis Intervention Services")}
                    className="hover:text-cyan-400"
                  >
                    Crisis Hotline
                  </a>
                </li>
                <li>
                  <a
                    href={getResourceCategoryUrl(
                      "Primary Mental Health Providers"
                    )}
                    className="hover:text-cyan-400"
                  >
                    Mental Health
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">FOLLOW US</h5>
              <div className="flex space-x-3">
                <a
                  href="https://www.facebook.com/profile.php?id=61559004021648"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-blue-600 hover:bg-blue-700 rounded flex items-center justify-center transition-colors duration-200"
                >
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-4 text-center">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Recovery Compass NH. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VeteranResources;
